import { useState } from 'react';
import { motion } from 'framer-motion';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    sujet: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('https://submit-form.com/1rVVW4S2w', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nom: '',
          email: '',
          telephone: '',
          sujet: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Erreur:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-4">
            CONTACTEZ-NOUS
          </span>
          <h2 className="font-serif text-4xl lg:text-5xl font-bold text-primary mb-4">
            Une Question ? Parlons-en !
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Notre équipe est à votre écoute pour répondre à toutes vos questions et vous accompagner dans vos projets logistiques vers l'Afrique.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="bg-white rounded-2xl p-8 shadow-lg text-center hover:shadow-xl transition-all"
          >
            <div className="w-16 h-16 flex items-center justify-center bg-accent/10 rounded-full mx-auto mb-4">
              <i className="ri-phone-line text-3xl text-accent"></i>
            </div>
            <h3 className="text-xl font-bold text-primary mb-2">Téléphone</h3>
            <p className="text-gray-600 text-sm mb-3">Appelez-nous directement</p>
            <a href="tel:+33695894452" className="text-accent hover:text-accent-dark transition-colors cursor-pointer font-medium text-sm">
              +33 6 95 89 44 52
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="bg-white rounded-2xl p-8 shadow-lg text-center hover:shadow-xl transition-all"
          >
            <div className="w-16 h-16 flex items-center justify-center bg-accent/10 rounded-full mx-auto mb-4">
              <i className="ri-mail-line text-3xl text-accent"></i>
            </div>
            <h3 className="text-xl font-bold text-primary mb-2">Email</h3>
            <p className="text-gray-600 text-sm mb-3">Écrivez-nous directement</p>
            <a href="mailto:atlastransport2026@gmail.com" className="text-accent hover:text-accent-dark transition-colors cursor-pointer font-medium text-sm">
              atlastransport2026@gmail.com
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="bg-white rounded-2xl p-8 shadow-lg text-center hover:shadow-xl transition-all"
          >
            <div className="w-16 h-16 flex items-center justify-center bg-accent/10 rounded-full mx-auto mb-4">
              <i className="ri-map-pin-line text-3xl text-accent"></i>
            </div>
            <h3 className="text-xl font-bold text-primary mb-2">Adresse</h3>
            <p className="text-gray-600 text-sm mb-3">Visitez nos bureaux</p>
            <p className="text-gray-700 font-medium text-sm">19 Allée des Demoiselles d'Avignon<br />92000 Nanterre</p>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="bg-white rounded-2xl p-8 lg:p-12 shadow-2xl max-w-4xl mx-auto border border-gray-100"
        >
          <div className="mb-8 text-center">
            <h3 className="text-2xl font-bold text-primary mb-2">Envoyez-nous un Message</h3>
            <p className="text-gray-600">Remplissez le formulaire ci-dessous et nous vous répondrons rapidement</p>
          </div>

          <form id="contact-form" data-readdy-form onSubmit={handleSubmit}>
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="nom" className="block text-sm font-semibold text-gray-700 mb-2">
                    Nom Complet *
                  </label>
                  <input
                    type="text"
                    id="nom"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent text-sm transition-all"
                    placeholder="Votre nom complet"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent text-sm transition-all"
                    placeholder="votre@email.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="telephone" className="block text-sm font-semibold text-gray-700 mb-2">
                    Téléphone
                  </label>
                  <input
                    type="tel"
                    id="telephone"
                    name="telephone"
                    value={formData.telephone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent text-sm transition-all"
                    placeholder="+33 6 12 34 56 78"
                  />
                </div>
                <div>
                  <label htmlFor="sujet" className="block text-sm font-semibold text-gray-700 mb-2">
                    Sujet *
                  </label>
                  <select
                    id="sujet"
                    name="sujet"
                    value={formData.sujet}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent text-sm transition-all"
                  >
                    <option value="">Sélectionner un sujet</option>
                    <option value="information">Demande d'information</option>
                    <option value="devis">Question sur un devis</option>
                    <option value="suivi">Suivi de colis</option>
                    <option value="reclamation">Réclamation</option>
                    <option value="partenariat">Partenariat</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  maxLength={500}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent text-sm resize-none transition-all"
                  placeholder="Décrivez votre demande en détail..."
                ></textarea>
                <div className="text-xs text-gray-500 mt-1 text-right">
                  {formData.message.length}/500 caractères
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-accent hover:bg-accent-dark text-white py-4 rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill"></i>
                    Envoyer le Message
                  </>
                )}
              </button>

              {submitStatus === 'success' && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm flex items-start gap-3"
                >
                  <i className="ri-checkbox-circle-fill text-xl flex-shrink-0"></i>
                  <div>
                    <strong>Message envoyé avec succès !</strong>
                    <p className="mt-1">Merci pour votre message. Notre équipe vous répondra dans les plus brefs délais.</p>
                  </div>
                </motion.div>
              )}

              {submitStatus === 'error' && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm flex items-start gap-3"
                >
                  <i className="ri-error-warning-fill text-xl flex-shrink-0"></i>
                  <div>
                    <strong>Erreur d'envoi</strong>
                    <p className="mt-1">Une erreur est survenue. Veuillez réessayer ou nous contacter directement par email.</p>
                  </div>
                </motion.div>
              )}
            </div>
          </form>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="mt-16 text-center"
        >
          <div className="inline-flex items-center gap-8 bg-white rounded-2xl px-8 py-6 shadow-lg">
            <div className="flex items-center gap-3">
              <i className="ri-time-line text-2xl text-accent"></i>
              <div className="text-left">
                <div className="text-sm font-semibold text-gray-700">Réponse Rapide</div>
                <div className="text-xs text-gray-500">Sous 24h maximum</div>
              </div>
            </div>
            <div className="w-px h-12 bg-gray-200"></div>
            <div className="flex items-center gap-3">
              <i className="ri-shield-check-line text-2xl text-accent"></i>
              <div className="text-left">
                <div className="text-sm font-semibold text-gray-700">Données Sécurisées</div>
                <div className="text-xs text-gray-500">Confidentialité garantie</div>
              </div>
            </div>
            <div className="w-px h-12 bg-gray-200"></div>
            <div className="flex items-center gap-3">
              <i className="ri-customer-service-2-line text-2xl text-accent"></i>
              <div className="text-left">
                <div className="text-sm font-semibold text-gray-700">Support Dédié</div>
                <div className="text-xs text-gray-500">Équipe à votre écoute</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactSection;
